/* procedure utility functions file */
import find from 'lodash/find';
import {createSelector} from 'reselect';
import {v4} from 'uuid';

import {
  getProcedureDescription,
  getProcedureSteps,
} from 'app/procedures/selectors';
import {gt} from 'app/utils/i18n';
import {removeDataImages} from 'app/utils/strings';

import type {ProceduresDashboardLocationParamsType} from './types';
import type {DefaultRootState} from 'react-redux';
import type {
  AnyStepType,
  AnyTaskStepType,
  ProcedureEndingStepType,
  ProcedureHashidSerializedType,
  ProcedureParsedContent,
  ProcedureSerializedType,
  ProcedureStepType,
  ProcedureVersionSerializedType,
  RawProcedureHashidSerializedType,
  RawProcedureSerializedType,
  RawProcedureVersionSerializedType,
} from 'sweet-types';

export const dashboardFilters = (
  reduxState: DefaultRootState
): ProceduresDashboardLocationParamsType => {
  const state = reduxState.proceduresPage;

  return {
    ordering: state.get('sortBy'),
    search: state.get('phrase') ?? undefined,
    team_id: state.get('team') ?? undefined,
    private:
      state.get('showPrivate') !== state.get('showPublic')
        ? state.get('showPrivate')
        : undefined,
  };
};

export const getDescription = (procedure: {description: string}) => {
  try {
    const description = JSON.parse(procedure.description);
    return validateDescription(description);
  } catch (e) {
    return validateDescription({
      content: procedure.description,
    });
  }
};

export const validateDescription = (description: Record<string, unknown>) => ({
  content: '',
  images: [],
  embeds: [],
  files: [],
  ...description,
});

export const validateStep = (step: any, n?: string | number) => ({
  /* ensures the step object have all required keys and the right type */
  n,
  content: '',
  images: [],
  files: [],
  embeds: [],
  id: step.id != null ? step.id : generateStepId(),
  ...step,
});

export const getContent = (content: string): ProcedureStepType[] => {
  let steps: ProcedureStepType[];

  try {
    steps = JSON.parse(content);
  } catch (e) {
    steps = [];
  }

  return normalizeSteps(steps);
};

export function normalizeSteps<T extends AnyStepType | AnyTaskStepType>(
  steps: T[]
): T[] {
  return removeOrphanChoices(steps).map(validateStep);
}

export const getContentWithOrphans = (content: string): ProcedureStepType[] => {
  let steps: ProcedureStepType[];

  try {
    steps = JSON.parse(content);
  } catch (e) {
    steps = [];
  }

  return steps.map(validateStep);
};

export const removeOrphanChoices = <T extends AnyStepType | AnyTaskStepType>(
  steps: T[]
): T[] => {
  const stepsIds = steps.map((step) => step.id);

  return steps.map<T>((step) => {
    const newStep: T = {...step};

    if (newStep.type === 'decision' && newStep.choices != null) {
      newStep.choices = newStep.choices.filter(
        (choice) => stepsIds.indexOf(choice.dest) > -1
      );

      if (newStep.choices.length === 0) {
        newStep.choices = [];
      }
    }

    return newStep;
  });
};

export const removeInlineImages = <
  T extends {
    content: string;
  }
>(
  step: T
): T => {
  return {...step, content: removeDataImages(step.content)};
};

export const generateStepId = () => v4().replace(/-/g, '');

export const hasFinalEnding = <I extends AnyStepType>(
  steps: Array<I>
): boolean => {
  const lastStep: I | null | undefined = steps[steps.length - 1];
  return lastStep != null && lastStep.type === 'ending';
};

export const shouldAddArtificialEnd = <I extends ProcedureStepType>(
  steps: Array<I>
): boolean => {
  const lastStep: I | null | undefined = steps[steps.length - 1];

  if (!lastStep) {
    return true;
  }

  return (
    !hasFinalEnding(steps) &&
    !(
      lastStep.type === 'content' &&
      lastStep.next != null &&
      find(steps, {
        id: lastStep.next,
      }) != null
    ) &&
    (lastStep.type !== 'decision' ||
      lastStep.choices == null ||
      lastStep.choices.length === 0)
  );
};

export const getNextStep = <I extends ProcedureStepType>(
  step: I,
  index: number,
  steps: Array<I>
) =>
  ('next' in step
    ? find(steps, {
        id: step.next,
      })
    : null) || steps[index + 1];

export const ARTIFICIAL_ENDING_ID = 'artificial-ending';

export const getArtificialEndingStep = (
  n: number | null
): ProcedureEndingStepType => ({
  id: ARTIFICIAL_ENDING_ID,
  content: '',
  type: 'ending',
  n: n ?? -1,
  title: gt('End'),
});

/* efficiently check if the procedure structure (links between steps) was changed */
export function procedureStructureIsEqual(
  stepsA: Array<ProcedureStepType>,
  stepsB: Array<ProcedureStepType>
): boolean {
  if (stepsA.length !== stepsB.length) {
    return false;
  }

  for (let i = 0; i < stepsA.length; i++) {
    const stepA = stepsA[i];
    const stepB = stepsB[i];

    if (
      stepA.type !== stepB.type ||
      stepA.id !== stepB.id ||
      (stepA as any).next !== (stepB as any).next
    ) {
      return false;
    }

    if ((stepA as any).choices != null || (stepB as any).choices != null) {
      const stepAchoices = (stepA as any).choices || [];
      const stepBchoices = (stepB as any).choices || [];

      if (stepAchoices.length !== stepBchoices.length) {
        return false;
      }

      for (let j = 0; j < stepAchoices.length; j++) {
        const choiceA = stepAchoices[j];
        const choiceB = stepBchoices[j];

        if (choiceA.id !== choiceB.id || choiceA.dest !== choiceB.dest) {
          return false;
        }
      }
    }
  }

  return true;
}

export function standardizeRawProcedure(
  doc: RawProcedureVersionSerializedType
): ProcedureVersionSerializedType;
export function standardizeRawProcedure(
  doc: RawProcedureHashidSerializedType
): ProcedureHashidSerializedType;
export function standardizeRawProcedure(
  doc: RawProcedureSerializedType
): ProcedureSerializedType;
export function standardizeRawProcedure(
  doc:
    | RawProcedureHashidSerializedType
    | RawProcedureSerializedType
    | RawProcedureVersionSerializedType
):
  | ProcedureHashidSerializedType
  | ProcedureSerializedType
  | ProcedureVersionSerializedType {
  return {
    ...doc,
    content: getContent(doc.content),
    description: getDescription(doc),
  };
}

export const formatPreviousProcedureVersion = createSelector<
  ProcedureVersionSerializedType | void,
  | (Omit<ProcedureVersionSerializedType, 'description' | 'content'> &
      ProcedureParsedContent)
  | undefined,
  ProcedureVersionSerializedType | void
>(
  (procedure) => procedure,
  (procedure) =>
    procedure != null
      ? {
          ...procedure,
          description: getProcedureDescription(procedure),
          content: getProcedureSteps(procedure),
        }
      : undefined
);

export const getDefaultAnswers = () => [
  {
    id: generateStepId(),
    text: '',
    correct: true,
  },
  {
    id: generateStepId(),
    text: '',
    correct: false,
  },
];
